<div
  class="max-w-4xl mx-auto bg-white shadow-sm rounded-lg border p-8 my-10 text-gray-700 mt-[150px]"
>
  <h1 class="text-2xl font-bold text-gray-900 mb-4 text-center">
    Terms and Conditions for Estate360 Community
  </h1>

  <p class="text-sm text-gray-500 text-center mb-8">
    Last Updated: November 11, 2025
  </p>

  <p class="mb-4">
    Please read these Terms of Use (“Terms”) carefully, as they set forth the
    legally binding terms and conditions between You (“You,” “Your,” or “User”)
    and Estate360 Community (“Estate360,” “We,” “Our,” or “Us”).
  </p>

  <p class="mb-4">
    These Terms govern your access to and use of the Estate360 Community mobile
    application, website, and any other digital platforms or services introduced
    by Estate360 (collectively, the “Platform”).
  </p>

  <p class="mb-4">
    By accessing or using the Platform, You agree to be bound by these Terms. If
    You do not agree, You must discontinue use immediately.
  </p>

  <p class="mb-6">
    Estate360 reserves the right, at its sole discretion, to modify or update
    these Terms at any time. Updates will be effective once posted on the
    Platform, and continued use indicates acceptance of such changes.
  </p>

  <div class="space-y-6">
    <section>
      <h2 class="font-semibold text-gray-800 text-lg mb-2">1. Eligibility</h2>
      <ul class="list-disc ml-6 space-y-1">
        <li>
          Be at least 18 years of age or have the consent of a legal guardian.
        </li>
        <li>
          Be a registered resident, estate manager, or authorized security
          personnel in an estate that uses Estate360.
        </li>
        <li>Provide accurate and complete registration information.</li>
      </ul>
      <p class="mt-2">
        Estate360 reserves the right to refuse access to any User who provides
        false information or violates these Terms.
      </p>
    </section>

    <section>
      <h2 class="font-semibold text-gray-800 text-lg mb-2">2. User Accounts</h2>
      <ul class="list-disc ml-6 space-y-1">
        <li>
          Users must create an account to access certain features of the
          Platform.
        </li>
        <li>
          You are responsible for maintaining the confidentiality of your login
          credentials and for all activities under your account.
        </li>
        <li>
          If You believe your account has been compromised, You must notify us
          immediately at
          <span class="text-blue-600 underline">support@estate360hub.com</span>.
        </li>
        <li>
          Estate360 will not be liable for any loss or damage resulting from
          unauthorized account use.
        </li>
      </ul>
    </section>

    <section>
      <h2 class="font-semibold text-gray-800 text-lg mb-2">
        3. Use of the Platform
      </h2>
      <p class="mb-2">
        You agree to use the Platform solely for lawful and authorized purposes,
        including:
      </p>
      <ul class="list-disc ml-6 space-y-1 mb-2">
        <li>Managing visitor access to your estate</li>
        <li>Approving or declining visitor entry</li>
        <li>Receiving security alerts and estate notifications</li>
        <li>Communicating with authorized estate personnel</li>
      </ul>
      <p class="mb-2">You agree not to:</p>
      <ul class="list-disc ml-6 space-y-1">
        <li>
          Misuse, copy, or redistribute the Platform’s content or software
        </li>
        <li>Upload false, misleading, or malicious information</li>
        <li>Interfere with or disrupt the Platform’s operations</li>
        <li>Attempt to gain unauthorized access to any part of the Platform</li>
      </ul>
    </section>

    <section>
      <h2 class="font-semibold text-gray-800 text-lg mb-2">4. Data Privacy</h2>
      <p>
        Your privacy is important to us. Estate360 handles all personal
        information in accordance with our Privacy Policy, which forms an
        integral part of these Terms. By using the Platform, You consent to the
        collection and use of your data as described in the Privacy Policy.
      </p>
    </section>

    <section>
      <h2 class="font-semibold text-gray-800 text-lg mb-2">
        5. Intellectual Property
      </h2>
      <p>
        All intellectual property rights in the Platform including but not
        limited to software, designs, text, logos, trademarks, and databases are
        owned by Estate360 or its licensors. You may not copy, modify,
        reproduce, or distribute any part of the Platform without our prior
        written permission.
      </p>
    </section>

    <section>
      <h2 class="font-semibold text-gray-800 text-lg mb-2">
        6. Service Availability and Updates
      </h2>
      <ul class="list-disc ml-6 space-y-1">
        <li>
          Estate360 strives to ensure uninterrupted service but does not
          guarantee that the Platform will always be available or error-free.
        </li>
        <li>
          We may suspend or modify the Platform for maintenance or updates.
        </li>
        <li>We may add, remove, or change features without prior notice.</li>
      </ul>
      <p class="mt-2">
        Estate360 is not liable for any disruption or data loss resulting from
        such updates or downtime.
      </p>
    </section>

    <section>
      <h2 class="font-semibold text-gray-800 text-lg mb-2">
        7. Limitation of Liability
      </h2>
      <ul class="list-disc ml-6 space-y-1">
        <li>
          Estate360 shall not be liable for any indirect, incidental, or
          consequential damages arising from the use or inability to use the
          Platform.
        </li>
        <li>
          We do not guarantee the accuracy of information provided by Users or
          third parties.
        </li>
        <li>
          Users are solely responsible for decisions made based on information
          from the Platform.
        </li>
      </ul>
    </section>

    <section>
      <h2 class="font-semibold text-gray-800 text-lg mb-2">8. Termination</h2>
      <p class="mb-2">
        Estate360 reserves the right to suspend or terminate your account
        without notice if:
      </p>
      <ul class="list-disc ml-6 space-y-1">
        <li>You breach these Terms</li>
        <li>You misuse the Platform or engage in fraudulent activity</li>
        <li>Required by law or by an estate’s management directive</li>
      </ul>
      <p class="mt-2">
        Upon termination, your right to access the Platform will immediately
        cease.
      </p>
    </section>

    <section>
      <h2 class="font-semibold text-gray-800 text-lg mb-2">
        9. Third-Party Services
      </h2>
      <p>
        The Platform may contain links or integrations with third-party tools
        (e.g., payment or verification services). Estate360 is not responsible
        for the content, privacy, or practices of such third parties. We
        recommend reviewing their respective terms and privacy policies before
        engaging their services.
      </p>
    </section>

    <section>
      <h2 class="font-semibold text-gray-800 text-lg mb-2">
        10. Indemnification
      </h2>
      <p>
        You agree to indemnify and hold Estate360, its affiliates, officers, and
        employees harmless from any claims, damages, or liabilities arising
        from:
      </p>
      <ul class="list-disc ml-6 space-y-1 mt-1">
        <li>Your breach of these Terms</li>
        <li>Your misuse of the Platform</li>
        <li>Your violation of any law or third-party rights</li>
      </ul>
    </section>

    <section>
      <h2 class="font-semibold text-gray-800 text-lg mb-2">
        11. Governing Law and Dispute Resolution
      </h2>
      <p>
        These Terms shall be governed by and construed in accordance with the
        laws of the Federal Republic of Nigeria. In the event of any dispute,
        both parties agree to first attempt resolution through good-faith
        negotiation. If unresolved, the matter shall be referred to mediation or
        arbitration in Lagos, Nigeria.
      </p>
    </section>

    <section>
      <h2 class="font-semibold text-gray-800 text-lg mb-2">
        12. Contact Information
      </h2>
      <p>
        If You have questions, concerns, or complaints regarding these Terms,
        please contact:
      </p>
      <div class="mt-2 space-y-1">
        <p>
          📧
          <a href="mailto:info@estate360hub.com" class="text-blue-600 underline"
            >info@estate360hub.com</a
          >
        </p>
        <p>📞 +2349065304004, +2348100163844</p>
      </div>
    </section>
  </div>

  <div class="text-center mt-10">
    <button
      class="bg-black hover:bg-zinc-500 text-white px-6 py-2 rounded-lg transition"
      (click)="navigateToHome()"
    >
      Back to Home
    </button>
  </div>
</div>
